import type { SocialSignal, NewsItem } from '@/types';

// ---------------------------------------------------------------------------
// Breaking Alert Engine
// Scans social signals (X/Telegram) + news for imminent threat signals
// ---------------------------------------------------------------------------

export type BreakingAlertLevel = 'flash' | 'breaking' | 'watch';

export interface BreakingAlert {
  id: string;
  level: BreakingAlertLevel;
  title: string;
  source: string;
  content: string;
  timestamp: string;
  dismissed: boolean;
  keywords: string[];
  audioPlayed: boolean;
}

// ── FLASH: 핵/WMD/선전포고 — 최고 경보, 모든 작업 중단 수준 ─────────────────
const FLASH_PATTERNS: RegExp[] = [
  /nuclear (strike|launch|attack|warhead|weapon|explosion)/i,
  /ballistic missile.{0,30}(launch|fired|incoming|detected)/i,
  /icbm.{0,20}(launch|detected|fired)/i,
  /(declaration|declaring).{0,10}war/i,
  /war.{0,10}declared/i,
  /martial law.{0,30}(declared|imposed|enacted)/i,
  /chemical (attack|weapon|agent).{0,20}(deployed|used|detected)/i,
  /biological (attack|weapon|agent).{0,20}(deployed|used|detected)/i,
  /dirty bomb/i,
  /nuclear plant.{0,30}(explosion|meltdown|breach)/i,
  /radiological (attack|device|dispersal)/i,
  /EMP (attack|strike|detonation)/i,
];

// ── BREAKING: 미사일 공격, 공습 경보, 철갑 발사 — 즉각 위협 ─────────────────
const BREAKING_PATTERNS: RegExp[] = [
  /air (raid|siren|alarm).{0,30}(activated|sounded|triggered)/i,
  /iron dome.{0,30}(firing|activated|intercept)/i,
  /patriot.{0,30}(intercept|launch|firing)/i,
  /(missile|rocket).{0,20}(strike|attack|impact|barrage|salvo)/i,
  /missiles?.{0,10}(fired|launched|incoming|in flight)/i,
  /rockets?.{0,10}(fired|launched|incoming|impact)/i,
  /(airstrike|air strike|bombing).{0,20}(confirmed|reported|underway)/i,
  /explosion.{0,30}(capital|city center|airport|port|base)/i,
  /coup.{0,20}(attempt|underway|succeeded|reported)/i,
  /president.{0,20}(assassinated|killed|overthrown)/i,
  /prime minister.{0,20}(assassinated|killed|shot)/i,
  /(naval|ground|military).{0,15}(invasion|offensive|incursion).{0,20}(launched|underway|confirmed)/i,
  /troops?.{0,10}(crossing|crossed).{0,20}border/i,
  /drone (swarm|attack|strike).{0,20}(confirmed|underway|launched)/i,
  /suicide (drone|uav|bomber).{0,20}(attack|impact|hit)/i,
  /hostage.{0,30}(crisis|situation|taken).{0,20}(confirmed|ongoing)/i,
  /(mass casualt|civilian casualt|people killed).{0,30}\d+/i,
  /emergency (broadcast|alert|declaration).{0,20}(issued|activated)/i,
  /evacuation.{0,20}(order|mandatory|immediate)/i,
];

// ── WATCH: 군사동향, 이동, 경고 수위 상향 ───────────────────────────────────
const WATCH_PATTERNS: RegExp[] = [
  /troops?.{0,20}(massing|mobilizing|deploying|moving).{0,30}border/i,
  /military (buildup|convoy|mobilization)/i,
  /ceasefire.{0,20}(collapsed|violated|broken|failed)/i,
  /(no-fly zone|flight ban).{0,20}(declared|imposed|activated)/i,
  /border.{0,10}(closed|closure|sealed)/i,
  /ambassador.{0,20}(expelled|recalled|summoned)/i,
  /breaking:.{0,100}(attack|explosion|clash|fired)/i,
  /alert.{0,20}(raised|elevated|maximum)/i,
  /sanctions.{0,20}(imposed|announced|expanded)/i,
  /oil (embargo|blockade|cutoff)/i,
];

let _alertCounter = 0;
function genId(): string {
  return `breaking-${Date.now()}-${++_alertCounter}`;
}

function matchLevel(text: string): { level: BreakingAlertLevel; keywords: string[] } | null {
  const matched: string[] = [];

  for (const rx of FLASH_PATTERNS) {
    const m = text.match(rx);
    if (m) matched.push(m[0].slice(0, 40));
  }
  if (matched.length) return { level: 'flash', keywords: matched };

  for (const rx of BREAKING_PATTERNS) {
    const m = text.match(rx);
    if (m) matched.push(m[0].slice(0, 40));
  }
  if (matched.length) return { level: 'breaking', keywords: matched };

  for (const rx of WATCH_PATTERNS) {
    const m = text.match(rx);
    if (m) matched.push(m[0].slice(0, 40));
  }
  if (matched.length) return { level: 'watch', keywords: matched };

  return null;
}

function buildTitle(level: BreakingAlertLevel, source: string, keywords: string[]): string {
  const kw = keywords[0] ?? '';
  if (level === 'flash')    return `⚡ FLASH — ${source}: ${kw.toUpperCase()}`;
  if (level === 'breaking') return `🔴 BREAKING — ${source}`;
  return `🟡 WATCH — ${source}`;
}

// ─── Main detection ────────────────────────────────────────────────────────

const DEDUP_WINDOW_MS = 4 * 60 * 60_000; // dismissed alerts expire from dedup after 4 hours

export function detectBreakingAlerts(
  signals: SocialSignal[],
  news: NewsItem[],
  existing: BreakingAlert[],
): BreakingAlert[] {
  const newAlerts: BreakingAlert[] = [];
  const now = Date.now();
  // Only dedup against: (1) active (not dismissed) alerts, or (2) dismissed within last 4h
  const seenContent = new Set(
    existing
      .filter((a) => !a.dismissed || now - new Date(a.timestamp).getTime() < DEDUP_WINDOW_MS)
      .map((a) => normalise(a.content)),
  );

  // Scan social signals (X / Telegram) — highest freshness
  for (const sig of signals.slice(0, 100)) {
    // 정적 샘플/데모 신호는 절대 알럿 생성 금지 (실제 계정 사칭 게시물)
    if (sig.id.startsWith('sig-sample-') || sig.id.startsWith('sig-static-')) continue;
    if (sig.severity === 'info' || sig.severity === 'low') continue;
    const text = sig.text;
    const match = matchLevel(text);
    if (!match) continue;

    const normalised = normalise(text);
    if (seenContent.has(normalised)) continue;
    seenContent.add(normalised);

    newAlerts.push({
      id: genId(),
      level: match.level,
      title: buildTitle(match.level, sig.platform === 'telegram' ? 'Telegram OSINT' : 'X (Twitter)', match.keywords),
      source: sig.author || sig.platform,
      content: text.slice(0, 280),
      timestamp: sig.timestamp,
      dismissed: false,
      keywords: match.keywords,
      audioPlayed: false,
    });

    if (newAlerts.length >= 3) break; // cap per cycle
  }

  // Scan top news items for breaking signals
  for (const item of news.slice(0, 30)) {
    const combined = `${item.title} ${item.description ?? ''}`;
    // Only process high/critical severity news
    if (item.severity !== 'critical' && item.severity !== 'high') continue;
    const match = matchLevel(combined);
    if (!match || match.level === 'watch') continue; // only flash/breaking from news

    const normalised = normalise(item.title);
    if (seenContent.has(normalised)) continue;
    seenContent.add(normalised);

    newAlerts.push({
      id: genId(),
      level: match.level,
      title: buildTitle(match.level, item.source, match.keywords),
      source: item.source,
      content: item.title,
      timestamp: item.publishedAt,
      dismissed: false,
      keywords: match.keywords,
      audioPlayed: false,
    });

    if (newAlerts.length >= 5) break;
  }

  return newAlerts;
}

function normalise(text: string): string {
  return text.toLowerCase().replace(/[^a-z0-9]/g, '').slice(0, 80);
}

// ─── Audio alert (Web Audio API — no external files) ──────────────────────

const MUTE_KEY = 'dx-alert-muted';

export function isAlertMuted(): boolean {
  try { return localStorage.getItem(MUTE_KEY) === '1'; } catch { return false; }
}

export function setAlertMuted(muted: boolean): void {
  try { localStorage.setItem(MUTE_KEY, muted ? '1' : '0'); } catch { /* ignore */ }
}

// 부드러운 알림음 — 낮은 볼륨 (0.08), sine 파형만 사용
export function playAlertSound(level: BreakingAlertLevel): void {
  if (isAlertMuted()) return;
  try {
    const ctx = new AudioContext();
    // 모바일: AudioContext가 suspended 상태로 생성될 수 있음
    if (ctx.state === 'suspended') void ctx.resume();
    const patterns: { freq: number; duration: number; gap: number }[] =
      level === 'flash'
        ? [
            { freq: 880, duration: 0.25, gap: 0.05 },
            { freq: 880, duration: 0.25, gap: 0.05 },
            { freq: 1320, duration: 0.5, gap: 0 },
          ]
        : level === 'breaking'
        ? [
            { freq: 660, duration: 0.2, gap: 0.08 },
            { freq: 660, duration: 0.2, gap: 0.08 },
            { freq: 880, duration: 0.35, gap: 0 },
          ]
        : [
            { freq: 440, duration: 0.15, gap: 0.1 },
            { freq: 550, duration: 0.3, gap: 0 },
          ];

    let startTime = ctx.currentTime + 0.05;
    for (const p of patterns) {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.frequency.value = p.freq;
      osc.type = 'sine';
      gain.gain.setValueAtTime(0.08, startTime);
      gain.gain.exponentialRampToValueAtTime(0.001, startTime + p.duration);
      osc.start(startTime);
      osc.stop(startTime + p.duration);
      startTime += p.duration + p.gap;
    }

    // Clean up after playback
    setTimeout(() => ctx.close(), (startTime - ctx.currentTime + 0.5) * 1000);
  } catch {
    // Silently fail if audio blocked (browser policy requires user gesture first)
  }
}
